import React, { useEffect, useState } from "react";
import { InlineLoading, Loading, Modal, TextArea, Toggle } from "carbon-components-react";

import APIClient from "web_modules/APIClient";
import { HurdleNoteKinds, TRUSEventShape } from "web_modules/Events/types";
import { DEFAULT_HURDLE } from "web_modules/shared/Header/ToolPanel/shared";

import { PipelineFieldsContext } from "web_modules/reporting_dashboard/PipelinePage/pipelineFieldsContext";

const styles = require("./styles.module.scss");

/**
 *  API helper functions
 */
const fetchMostRecentPinnedMessageForAppID = appID =>
  APIClient.get(`/uw/${appID}/pinned_message`).then(({ pinned_message }) => {
    return { pinnedMsg: pinned_message };
  }) as Promise<{ pinnedMsg: string | null }>;

const createNewPinnedMessageForAppID = (
  appId,
  note: string,
  pinned,
  hurdleName = DEFAULT_HURDLE,
) => {
  const hurdleNameTruncated = hurdleName.replace("Hurdle", "");
  return APIClient.post(`/uw/${appId}/events`, {
    appId,
    kind: HurdleNoteKinds[hurdleNameTruncated],
    fields: {
      note,
      pinned,
    },
  }) as Promise<{ event: TRUSEventShape }>;
};

type Props = {
  appID: string;
  onClose: Function;
};

const CreatePinnedNoteModal = ({ appID, onClose }: Props) => {
  /**
   *  UI state
   */
  const [modalIsOpen, setModalIsOpen] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [pendingNoteCreation, setPendingNoteCreation] = useState(false);

  /**
   * Data fetching & updating
   */
  const [loadedMsg, setLoadedMsg] = useState(null);
  const [formMsg, setFormMsg] = useState("");
  const [formPinned, setFormPinned] = useState(true);

  useEffect(() => {
    setFormMsg(loadedMsg);
  }, [loadedMsg]);

  useEffect(() => {
    fetchMostRecentPinnedMessageForAppID(appID).then(({ pinnedMsg }) => {
      setLoaded(true);
      setLoadedMsg(pinnedMsg);
    });
  }, [appID]);

  /**
   *  helper functions
   */
  const reset = () => {
    setModalIsOpen(false);
    onClose();
  };

  const createNewNote = async updatePinnedMessage => {
    setPendingNoteCreation(true);

    try {
      const { event } = await createNewPinnedMessageForAppID(appID, formMsg, formPinned);
      const { fields } = event;
      const { note, pinned } = fields;

      if (pinned) {
        updatePinnedMessage(appID, note);
      }
    } catch (e) {
      alert("There was an error saving your new note. Please contact Eng.");
    } finally {
      setPendingNoteCreation(false);
      reset();
    }
  };

  const renderNoteForm = () => (
    <div>
      <h2>
        <p>*Creating a new pinned note will overwrite the previous pinned note in the pipeline</p>
        <Toggle
          toggled={formPinned}
          onToggle={() => setFormPinned(!formPinned)}
          labelA="regular note"
          labelB="pinned note"
        />
        <br />
        <TextArea cols="50" rows="2" value={formMsg} onChange={e => setFormMsg(e.target.value)} />
      </h2>
    </div>
  );

  return (
    <PipelineFieldsContext.Consumer>
      {({ updatePinnedMessage }) => (
        <Modal
          modalHeading={`Create note for loan app ${appID}`}
          primaryButtonText={pendingNoteCreation ? <InlineLoading status="active" /> : "Create"}
          primaryButtonDisabled={pendingNoteCreation || !loaded}
          secondaryButtonText="Cancel"
          size="sm"
          open={modalIsOpen}
          onBlur={reset}
          onRequestClose={reset}
          onRequestSubmit={() => createNewNote(updatePinnedMessage)}
          className={styles.modalContactsForm}
        >
          {loaded ? (
            renderNoteForm()
          ) : (
            <Loading
              description="Active loading indicator"
              withOverlay={false}
              style={{ margin: "0 auto" }}
            />
          )}
        </Modal>
      )}
    </PipelineFieldsContext.Consumer>
  );
};
export default CreatePinnedNoteModal;
